import { useState } from 'react';
import useSWR from 'swr';
import { Copy, Check, Loader2, LinkIcon, RefreshCw, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, scanReorderReminders } from '@/lib/api';

const fetchOrders = async () => {
  const { data } = await apiClient.get('/admin/orders');
  return data;
};

const createLink = async (orderId) => {
  const { data } = await apiClient.post(`/admin/orders/${orderId}/fast-order-link`);
  return data;
};

const format = (n) => `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

export default function OrdersPanel() {
  const { data: orders = [], isLoading, mutate } = useSWR('/admin/orders', fetchOrders);
  const [copiedFor, setCopiedFor] = useState(null);
  const [busy, setBusy] = useState(null);
  const [scanning, setScanning] = useState(false);

  const onScanReminders = async () => {
    if (scanning) return;
    setScanning(true);
    const id = toast.loading('Scanning subscriptions for 48h reminders…');
    try {
      const res = await scanReorderReminders();
      toast.success(
        `Reorder scan · ${res.scanned} subs · ${res.eligible} eligible · ${res.sent} sent · ${res.already_sent} already sent`,
        { id },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reorder scan failed', { id });
    } finally {
      setScanning(false);
    }
  };

  const onGenerate = async (order) => {
    setBusy(order.id);
    try {
      const res = await createLink(order.id);
      await navigator.clipboard.writeText(res.url);
      setCopiedFor(order.id);
      toast.success('Fast-order link copied to clipboard');
      setTimeout(() => setCopiedFor(null), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create link');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-3xl">Orders</h2>
          <p className="text-white/55 text-sm mt-1 max-w-md">
            Every paid Stripe order plus initiated carts. Generate a fast-order share link for repeat wholesale customers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onScanReminders}
            disabled={scanning}
            data-testid="admin-scan-reorder-reminders"
            title="Send 48h subscription pre-delivery reminders now"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] border border-white/15 hover:border-white text-white px-4 py-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {scanning ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />}
            {scanning ? 'Scanning…' : 'Reorder reminders'}
          </button>
          <button
            type="button"
            onClick={() => mutate()}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] border border-white/15 hover:border-white text-white px-4 py-2 transition-colors"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Loader2 size={28} className="animate-spin text-white/50" />
        </div>
      ) : orders.length === 0 ? (
        <div className="border border-dashed border-white/15 p-10 text-center text-white/50">
          No orders yet.
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.28em] text-white/45 bg-white/[0.02]">
            <div className="col-span-3">Order</div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Total</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-white/[0.02] transition-colors">
              <div className="col-span-12 md:col-span-3">
                <div className="font-display text-lg">{o.id}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mt-0.5">
                  {fmtDate(o.created_at)} · {o.items_count} item{o.items_count === 1 ? '' : 's'}
                </div>
              </div>
              <div className="col-span-12 md:col-span-3 text-sm">
                <div className="text-white">{o.customer_name || '—'}</div>
                <div className="text-white/50 text-[11px]">{o.customer_email}</div>
              </div>
              <div className="col-span-6 md:col-span-2">
                <span className={`inline-block text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${
                  (o.payment_status === 'paid' || o.status === 'paid')
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                    : 'border-white/15 bg-white/5 text-white/60'
                }`}>{o.payment_status || o.status || '—'}</span>
              </div>
              <div className="col-span-6 md:col-span-2 md:text-right">
                <div className="font-display text-lg tabular-nums">{format(o.total)}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{o.currency || 'AUD'}</div>
              </div>
              <div className="col-span-12 md:col-span-2 md:text-right">
                <button
                  type="button"
                  onClick={() => onGenerate(o)}
                  disabled={busy === o.id}
                  data-testid={`admin-fast-order-${o.id}`}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] border border-white/25 hover:border-[var(--mw-primary)] hover:text-[var(--mw-primary)] disabled:opacity-60 text-white px-3 py-2 transition-colors"
                >
                  {busy === o.id ? <Loader2 size={11} className="animate-spin" /> :
                    copiedFor === o.id ? <Check size={11} className="text-emerald-400" /> : <LinkIcon size={11} />}
                  {copiedFor === o.id ? 'Copied' : 'Fast-order link'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
